import type { COLOR } from 'vars/color'

import { THEME_WRAPPER_CLASSNAME } from './constants'

export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

export const parseVariables = (variables: string) => {
  return +variables.split('-').slice(-1) / 2.5 // --fixed-indents-40 -> 40 -> 40/2.5 -> 16 (as number)
}

/**
 * Function checks if a string is a variable
 * @param str
 */
export const isVariable = (str: unknown): boolean | undefined => {
  if (!str || typeof str !== 'string') return

  return str.startsWith('--')
}

interface CardCountProps {
  clientWidth: number
  cardsInRow: number
  rowDirection: string
  setVisibleCards: (i: number) => void
  tvChannelCard: boolean
  withSpecialCell?: boolean
}

export const setMobileCardsCount = ({
  clientWidth,
  cardsInRow,
  rowDirection,
  setVisibleCards,
  tvChannelCard,
}: CardCountProps) => {
  const exceptionMobile =
    cardsInRow === 6 &&
    rowDirection === 'H' &&
    clientWidth < 600 &&
    !tvChannelCard

  if (exceptionMobile) {
    return setVisibleCards(cardsInRow - 3)
  }

  if (cardsInRow === 6 && tvChannelCard) {
    return setVisibleCards(cardsInRow - 4)
  }

  if (cardsInRow === 8 && tvChannelCard) {
    return setVisibleCards(cardsInRow - 6)
  }
  if (cardsInRow === 2) {
    return setVisibleCards(cardsInRow - 1)
  }
  if (cardsInRow > 2 && cardsInRow !== 5) {
    return setVisibleCards(cardsInRow - 2)
  }
  if (cardsInRow === 5) {
    if (rowDirection === 'V') {
      return setVisibleCards(cardsInRow - 2)
    }
    if (rowDirection === 'H') {
      return setVisibleCards(cardsInRow - 3)
    }
  }
}

export const setTabletCardsCount = ({
  clientWidth,
  cardsInRow,
  rowDirection,
  setVisibleCards,
  tvChannelCard,
  withSpecialCell,
}: CardCountProps) => {
  const exceptionTablet =
    cardsInRow === 6 &&
    rowDirection === 'H' &&
    clientWidth >= 600 &&
    clientWidth < 768 &&
    !tvChannelCard

  const withSpecialCellTablet =
    cardsInRow > 2 && cardsInRow !== 5 && withSpecialCell && clientWidth > 768

  if (exceptionTablet) {
    return setVisibleCards(cardsInRow - 2)
  }
  if (tvChannelCard) {
    if (cardsInRow === 6) {
      return setVisibleCards(cardsInRow - 2)
    }

    if (cardsInRow === 8 && clientWidth >= 600 && clientWidth < 768) {
      return setVisibleCards(cardsInRow - 4)
    }
    if (cardsInRow === 8) {
      return setVisibleCards(cardsInRow - 2)
    }
  }

  if (cardsInRow === 2 || withSpecialCellTablet) {
    return setVisibleCards(cardsInRow)
  }

  if (
    cardsInRow > 2 &&
    cardsInRow !== 5 &&
    withSpecialCell &&
    clientWidth < 768
  ) {
    return setVisibleCards(cardsInRow - 2)
  }
  if (cardsInRow > 2 && cardsInRow !== 5) {
    return setVisibleCards(cardsInRow - 1)
  }
  if (cardsInRow === 5) {
    if (rowDirection === 'V') {
      return setVisibleCards(cardsInRow - 1)
    }
    if (rowDirection === 'H') {
      return setVisibleCards(cardsInRow - 2)
    }
  }
}

export const setMobileGridCount = ({
  // clientWidth,
  cardsInRow,
  rowDirection,
  setVisibleCards,
  tvChannelCard,
}: CardCountProps) => {
  if (cardsInRow === 6 && tvChannelCard) {
    return setVisibleCards(cardsInRow - 4)
  }

  if (cardsInRow === 8 && tvChannelCard) {
    return setVisibleCards(cardsInRow - 6)
  }
  if (rowDirection === 'V') {
    return setVisibleCards(cardsInRow - 2)
  }
  if (rowDirection === 'H' && cardsInRow === 2) {
    return setVisibleCards(cardsInRow - 1)
  }
  if (rowDirection === 'H' && cardsInRow > 2 && cardsInRow < 5) {
    return setVisibleCards(cardsInRow - 2)
  }
  if (rowDirection === 'H' && cardsInRow >= 5) {
    return setVisibleCards(cardsInRow - 3)
  }
}

export const setTabletGridCount = ({
  clientWidth,
  cardsInRow,
  rowDirection,
  setVisibleCards,
  tvChannelCard,
}: CardCountProps) => {
  if (tvChannelCard) {
    if (cardsInRow === 6) {
      return setVisibleCards(cardsInRow - 2)
    }

    if (cardsInRow === 8 && clientWidth >= 600 && clientWidth < 768) {
      return setVisibleCards(cardsInRow - 4)
    }
    if (cardsInRow === 8) {
      return setVisibleCards(cardsInRow - 2)
    }
  }
  if (rowDirection === 'V') {
    setVisibleCards(cardsInRow - 1)
  }
  if (rowDirection === 'H' && cardsInRow <= 3) {
    return setVisibleCards(cardsInRow - 1)
  }
  if (rowDirection === 'H' && cardsInRow >= 4) {
    if (clientWidth >= 768) {
      return setVisibleCards(cardsInRow - 1)
    } else {
      return setVisibleCards(cardsInRow - 2)
    }
  }
}

/**
 * combineFirstLettersOfWordsToString helper combines the first letters of words into a single string
 * @param string the string to be formatted
 * @param wordsNumber number of words to take the first letter from
 */
export const combineFirstLettersOfWordsToString = (
  string: string,
  wordsNumber?: number
): string => {
  if (!string) return ''

  const array = string.split(' ')
  return array
    .slice(0, wordsNumber || array.length - 1)
    .map((item) => item.charAt(0))
    .join('')
}

/**
 * identifyTouchDevice helper identify touch devices
 * @returns boolean
 */
export const identifyTouchDevice = (): boolean | undefined => {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    ('ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0))
  )
}

export const stringToHash = (string: string) => {
  const SHIFT = 5
  let hash = 0
  for (let i = 0, l = string?.length; i < l; i++) {
    hash = string.charCodeAt(i) + ((hash << SHIFT) - hash)
  }
  return hash
}

export const getAvatarColor = (name: string, list: string[] = []) => {
  const avatarNameHash = stringToHash(name)
  const avatarId = Math.abs(avatarNameHash) % list.length
  return list[avatarId]
}

/**
 * Debounce function: can be used, for example, to prevent multi clicking
 * @param fn function to be executed with a timeout
 * @param delay
 * @returns () => void
 */
export const debounce = (
  fn: (...args: any) => void,
  delay: number
): ((...args: any) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>

  return (...args: any) => {
    clearInterval(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

/**
 * getValueForBreakpoint helper return number according to breakpoints
 * @param clientWidth number
 * @param breakpoints object
 * @returns any
 */
export const getValueForBreakpoint = <D extends Record<string, number>>(
  clientWidth: number,
  breakpoints: D
): D[keyof D] => {
  let value = undefined as unknown as D[keyof D]
  for (const key in breakpoints) {
    if (clientWidth > +key) value = breakpoints[key]
  }
  return value
}

export const isGradient = (color: COLOR) => {
  const root = document.querySelector(`.${THEME_WRAPPER_CLASSNAME}`)

  if (root) {
    const style = getComputedStyle(root)

    const colorValue = style.getPropertyValue(color)

    return colorValue.includes('linear-gradient')
  }
}
